// src/pages/Dashboard.jsx
import { reasonText, incomingReasonText } from '../utils/faxReasons';
import { useState, useEffect, useCallback, useRef } from 'react';
import SendFaxModal from '../components/SendFaxModal';
import Pager, { useServerPages } from '../components/Pager';
import FaxDetailPanel from '../components/FaxDetailPanel';
import { useNotify } from '../components/Notifications';
import FaxProgress from '../components/FaxProgress';
import { useLiveOutgoing } from '../hooks/useLiveOutgoing';
import { useIncomingArrivals, ARRIVALS_POLL_MS } from '../hooks/useIncomingArrivals';
import { useNewFaxes } from '../hooks/useNewFaxes';
import { takeOpenFax } from '../utils/openFaxRequest';
import { usePreferences } from '../hooks/usePreferences';
import SortTh, { useSort } from '../components/SortTh';
import MultiSearchSelect from '../components/MultiSearchSelect';
import { useAuth } from '../hooks/useAuth';
import ResetFiltersButton, { filtersDiffer } from '../components/ResetFiltersButton';
import EmptyState from '../components/EmptyState';
import { fetchOutgoing, fetchIncoming, getDownloadUrl, downloadReport, deleteFax, downloadZip } from '../utils/api';
import { STATUS_COLORS, formatFax, formatDate, formatShortDate, formatCardDate, formatRetryWindowDay, outStatus } from '../utils/format';
import { useContactNames } from '../hooks/useContactNames';
import { timeZone, tzLabel } from '../utils/timeZone';
import TeamFilter from '../components/TeamFilter';
import { PAGE_TITLE } from '../theme/tabs';
import PriorityStar, { usePriorityNumbers } from '../components/PriorityStar';
import Loader from '../components/Loader';
import Emo from '../components/Emo';
import { usePhone } from '../hooks/useWindowWidth';
import { useClientWord } from '../hooks/useClientWord';
import { useSendTimings } from '../hooks/useSendTimings';

// Incoming opens on Received; users pick All or Failed deliberately
const DEFAULT_FILTERS = {
  startDate: '', endDate: '', status: '', fromEmail: [], toNumber: [], fromNumber: [],
  incomingStatus: 'received', limit: '50', team: ''
};

export default function Dashboard({ activeTab }) {
  const atCo = useClientWord() === 'Client' ? 'the client' : 'your company';   // 3.67.5: the people at a client read 'your company'
  // 3.51.0: phones: faxes as cards, the search filters behind a Filters button
  const phone = usePhone();
  const tm = useSendTimings();   // 3.75.0: Sending Timings, as words
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { getToken } = useAuth();
  const { notify, confirm } = useNotify();
  const { prefs } = usePreferences();
  const prefsRef  = useRef(prefs);
  prefsRef.current = prefs;
  // Defaults for this person: their preferred list size
  const defaultFilters = { ...DEFAULT_FILTERS, limit: prefs.listSize };
  const tab = activeTab;
  // What this person's role allows (Roles & Access). "Sent By" only
  // matters to people who see other people's outgoing faxes.
  const { can, shows, effectiveInfo, viewAs } = useAuth();
  // "All accounts" (Super Admins): each row says which account it's from
  const showAccount = effectiveInfo?.accountId === 'all';
  const isAdmin    = effectiveInfo?.roleId === 'super_admin';
  const seesOthers = isAdmin || effectiveInfo?.perms?.['outgoing.scope'] === 'group';
  // Teams (3.19.3): a Team filter when the picked client has teams; a Team
  // column whenever the rows carry team names
  const clientTeams = effectiveInfo?.clientTeams || [];

  const [data, setData]               = useState([]);
  const showTeam = clientTeams.length > 0 || data.some(f => (f.teamNames || []).length);
  // Column sorting on the faxes shown (3.16.0)
  const { sorted: sortedData, sort, onSort } = useSort(data, {
    account: f => f.accountName, team: f => (f.teamNames || []).join(', '), from: f => (tab === 'outgoing' ? f.fromEmail : String(f.fromNumber || '').replace(/\D/g, '')),
    to: f => String(f.toNumber || '').replace(/\D/g, ''), file: f => f.fileName, pages: f => Number(f.pages) || 0,
    date: f => (tab === 'outgoing' ? f.sentAt : f.receivedAt) || '', delivered: f => f.deliveredAt || '', status: f => f.status,
  });
  // Page by page (3.26.0): the server sends one page; sorting by a column or
  // Last loads every matching fax once (components/Pager)
  const loadRef = useRef(null), lastFiltersRef = useRef(null);
  const pages = useServerPages(sortedData, lastFiltersRef.current?.limit || '50', req => loadRef.current?.(lastFiltersRef.current || undefined, req));
  const sortAll = key => { onSort(key); pages.sortAll(); };
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [selected, setSelected]       = useState(new Set());
  const [downloading, setDownloading] = useState(new Set());
  const [showSendFax, setShowSendFax]   = useState(false);
  const [resending, setResending]       = useState(null);
  const [openId, setOpenId]             = useState(null);   // fax shown in the detail panel
  const [lineFax, setLineFax]           = useState(null);   // 3.69.4: a Resend in line, opened from the pop-up (its failed fax)
  const [appliedFilters, setAppliedFilters] = useState(null); // filters of the last search
  const [justFinished, setJustFinished] = useState(new Set()); // rows whose result just arrived
  const [filters, setFilters]         = useState(() => ({ ...DEFAULT_FILTERS, limit: prefs.listSize }));

  // Outgoing and incoming follow separate rules — each tab sends only its
  // own filters, so an outgoing status can never leak into incoming.
  function filtersFor(t, f) {
    const common = { startDate: f.startDate, endDate: f.endDate, toNumber: f.toNumber, limit: f.limit, team: f.team };
    return t === 'outgoing'
      ? { ...common, status: f.status, fromEmail: f.fromEmail }
      : { ...common, incomingStatus: f.incomingStatus, fromNumber: f.fromNumber };
  }

  // Failed incoming faxes have no document — nothing to download or select
  const isFailedIncoming = fax => tab === 'incoming' && fax.failedIncoming === true;

  const [waiting, setWaiting] = useState([]);   // Outbox: faxes waiting for a free line (3.22.3)
  const [priority, reloadPriority] = usePriorityNumbers();   // ★ priority numbers (3.22.3)

  const loadData = useCallback(async (f = filters, req = null) => {
    setLoading(true);
    setError('');
    setSelected(new Set());
    try {
      const applied = filtersFor(tab, f);
      lastFiltersRef.current = f;
      const query = { ...applied, ...pages.params(req, f.limit) };
      const result = tab === 'outgoing'
        ? await fetchOutgoing(query, getToken)
        : await fetchIncoming(query, getToken);
      setData(result.data || []);
      pages.loaded(req, result.hasMore, f.limit);
      // Outbox: faxes waiting for a free line (3.22.3)
      if (tab === 'outgoing') {
        try {
          const r = await fetch(`${require('../firebase/config').API_BASE}/portal/outgoing/waiting`, { headers: { Authorization: `Bearer ${await getToken()}` } });
          setWaiting((await r.json()).data || []);
        } catch { setWaiting([]); }
      } else setWaiting([]);
      setAppliedFilters(applied);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filters, getToken, pages.params, pages.loaded]);
  loadRef.current = loadData;

  // Faxes in line (3.36.5) update by themselves: every 30 seconds while
  // some wait, and whenever the live feed (App.jsx) says something changed
  // in Outbox. A fax that left the line has started: the new-fax check adds
  // its row at once (it never reloads what the person is looking at).
  const refreshWaiting = useCallback(async () => {
    try {
      const r = await fetch(`${require('../firebase/config').API_BASE}/portal/outgoing/waiting`, { headers: { Authorization: `Bearer ${await getToken()}` } });
      const next = (await r.json()).data || [];
      setWaiting(prev => {
        if (prev.some(w => !next.some(x => x.id === w.id))) setTimeout(() => checkNowRef.current?.(), 0);
        return next;
      });
    } catch { /* next time */ }
  }, [getToken]);
  const waitingKey = waiting.map(w => w.id).join(',');
  useEffect(() => {
    if (tab !== 'outgoing' || !waitingKey) return undefined;
    const t = setInterval(() => { if (!document.hidden) refreshWaiting(); }, 30 * 1000);
    return () => clearInterval(t);
  }, [tab, waitingKey, refreshWaiting]);
  useEffect(() => {
    if (tab !== 'outgoing') return undefined;
    let timer = null;
    const onActivity = e => {
      if (!e.detail?.outgoing) return;
      clearTimeout(timer);
      timer = setTimeout(() => { refreshWaiting(); checkNowRef.current?.(); }, 1500);
    };
    window.addEventListener('fax-activity', onActivity);
    return () => { clearTimeout(timer); window.removeEventListener('fax-activity', onActivity); };
  }, [tab, refreshWaiting]);

  // Back to this person's defaults and search again (Reset and Clear filters)
  // Mark all as seen (3.34.0)
  const [markingAll, setMarkingAll] = useState(false);
  async function markAllSeen() {
    setMarkingAll(true);
    try {
      const r = await fetch(`${require('../firebase/config').API_BASE}/portal/incoming/seen-all`, { method: 'POST', headers: { Authorization: `Bearer ${await getToken()}` } });
      if (r.ok) setData(prev => prev.map(x => (x.seen === false ? { ...x, seen: true } : x)));
    } catch { /* the list stays as it is */ }
    setMarkingAll(false);
  }

  function resetFilters() {
    setFilters(defaultFilters);
    loadData(defaultFilters);
  }

  // Did the last search filter anything out? Judged on the applied filters
  // (what produced the list); list size isn't a filter.
  const withoutLimit = f => ({ ...filtersFor(tab, f), limit: '' });
  const resultFiltered = !!appliedFilters && filtersDiffer(withoutLimit(appliedFilters), withoutLimit(defaultFilters));

  // Search selections are per-tab — clear them when switching tabs. The
  // incoming Status filter always opens on Received; users pick All or
  // Failed deliberately. 3.21.1: Control Panel → Clients "View faxes"
  // (/portal/incoming?to=<number>) opens on that number, all statuses.
  useEffect(() => {
    setOpenId(null);
    const q = tab === 'incoming' ? new URLSearchParams(window.location.search) : null;
    const to = q ? q.get('to') : null;
    // 3.49.18: the weekly "unread faxes" email links to /portal/inbox?unread=1 (Show unread ticked)
    const unread = q ? q.get('unread') === '1' : false;
    const cleared = { ...filters, fromEmail: [], toNumber: to ? [to.replace(/\D/g, '').slice(-10)] : [], fromNumber: [], incomingStatus: unread ? 'new' : to ? '' : 'received' };
    setFilters(cleared);
    loadData(cleared);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const selectable = pages.pageRows.filter(d => !isFailedIncoming(d));   // this page's faxes

  function toggleAll() {
    if (selected.size === selectable.length) setSelected(new Set());
    else setSelected(new Set(selectable.map(d => d.faxId)));
  }

  async function handleSingleDownload(fax) {
    setDownloading(prev => new Set(prev).add(fax.faxId));
    try {
      const { url, fileName } = await getDownloadUrl(tab, fax.faxId, getToken);
      triggerDownload(url, fileName);
    } catch (e) {
      notify({ tone: 'error', title: 'Download failed', detail: e.message });
    }
    setDownloading(prev => { const n = new Set(prev); n.delete(fax.faxId); return n; });
  }

  // Delete the ticked faxes (soft delete: they move to Purged Faxes)
  async function handleBulkDelete() {
    const ids = [...selected];
    if (!ids.length) return;
    const ok = await confirm({
      title: `Delete ${ids.length} fax${ids.length === 1 ? '' : 'es'}?`,
      message: `They're removed from this list for everyone at ${atCo} and moved to Trash.${tab === 'outgoing' ? ' Automatic retries of them stop.' : ''}`,
      confirmLabel: 'Delete', danger: true,
    });
    if (!ok) return;
    const gone = [], failed = [];
    for (const id of ids) {
      try { const r = await deleteFax(tab, id, getToken); gone.push(...(r.deleted || [id])); }
      catch { failed.push(id); }
    }
    setData(prev => prev.filter(r => !gone.includes(r.faxId)));
    setSelected(new Set(failed));
    if (failed.length) notify({ tone: 'error', title: `${failed.length} of ${ids.length} couldn't be deleted`, detail: 'Please try those again.' });
    else notify({ bell: 'fax', tone: 'success', title: `${ids.length} fax${ids.length === 1 ? '' : 'es'} deleted`, detail: 'Find them in Trash.' });
  }

  // Download Selected (3.34.6): one zip from the server instead of a download
  // per file; one file stays a plain download
  async function handleBulkDownload() {
    if (!selected.size) return;
    setLoading(true);
    try {
      if (selected.size === 1) {
        const { url, fileName } = await getDownloadUrl(tab, [...selected][0], getToken);
        triggerDownload(url, fileName);
      } else {
        await downloadZip([...selected].map(faxId => ({ type: tab, faxId })), getToken);
      }
    } catch (e) {
      notify({ tone: 'error', title: 'Download failed', detail: `${e.message} The files may have expired (they're kept 90 days).` });
    }
    setLoading(false);
  }

  async function handleResend(faxId) {
    const ok = await confirm({
      title: 'Resend this fax?',
      message: 'The fax will be queued and sent again to the same number.',
      confirmLabel: 'Resend',
    });
    if (!ok) return null;
    let newId = null;
    setResending(faxId);
    try {
      const token = await getToken();
      const res   = await fetch(`${require('../firebase/config').API_BASE}/portal/resend/${faxId}`, {
        method : 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Resend failed');
      newId = data.faxId;
      if (data.waiting) notify({ bell: 'fax', tone: 'success', title: 'Fax waiting for the line', detail: 'That number is busy with another fax. It goes out by itself as soon as the line is free (see Outbox).' });
      else notify({ bell: 'fax', tone: 'success', title: 'Fax queued to send again', detail: `New reference ID: ${data.faxId}`,
               onClick: () => setOpenId(newId) });
      loadData();
    } catch (e) {
      notify({ tone: 'error', title: 'Resend failed', detail: e.message });
    }
    setResending(null);
    return newId;
  }

  // Take back a scheduled or waiting fax (3.27.0)
  async function cancelWaiting(w) {
    const ok = await confirm({ title: 'Cancel this fax?', message: `${w.fileName || 'The fax'} to ${formatFax(w.toNumber)} won't be sent.`, confirmLabel: 'Cancel fax', cancelLabel: 'Keep it', danger: true });
    if (!ok) return;
    try {
      const r = await fetch(`${require('../firebase/config').API_BASE}/portal/outgoing/waiting/${encodeURIComponent(w.id)}/cancel`, { method: 'POST', headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Couldn't cancel it.");
      notify({ bell: 'fax', tone: 'success', title: 'Fax cancelled' });
      setWaiting(ws => ws.filter(x => x.id !== w.id));
    } catch (e) { notify({ tone: 'error', title: "Couldn't cancel the fax", detail: e.message }); }
  }

  // 3.69.4 (owner): a fax waiting in line goes out now (Admins and Managers)
  async function sendWaitingNow(w) {
    const ok = await confirm({ title: 'Send this fax now?', message: `${w.fileName || 'The fax'} goes to ${formatFax(w.toNumber)} now instead of waiting in line${w.slow ? `. This number is hard to reach, so faxes to it are normally spaced ${tm.hardSpacing} apart` : ''}. If a fax to that number is on the line right now, try again in a few minutes.`, confirmLabel: 'Send now' });
    if (!ok) return;
    try {
      const r = await fetch(`${require('../firebase/config').API_BASE}/portal/outgoing/waiting/${encodeURIComponent(w.id)}/send-now`, { method: 'POST', headers: { Authorization: `Bearer ${await getToken()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || "Couldn't send it now.");
      notify({ bell: 'fax', tone: 'success', title: 'Fax sent', detail: `To ${formatFax(w.toNumber)}` });
      setWaiting(ws => ws.filter(x => x.id !== w.id));
      setTimeout(() => checkNowRef.current?.(), 1500);
    } catch (e) { notify({ tone: 'error', title: "Couldn't send it now", detail: e.message }); }
  }

  async function handleReportDownload() {
    try {
      await downloadReport(tab, filtersFor(tab, filters), getToken);
    } catch (e) {
      notify({ tone: 'error', title: 'Report download failed', detail: e.message });
    }
  }

  function triggerDownload(url, fileName) {
    const a    = document.createElement('a');
    a.href     = url;
    a.download = fileName || 'fax-file';
    a.target   = '_blank';
    a.click();
  }

  function handleFilterChange(key, value) {
    setFilters(prev => ({ ...prev, [key]: value }));
  }

  // ── Live status updates (Outgoing tab) ────────────────────────
  // Pop-ups for these results now come from the portal-wide watcher
  // (hooks/useFaxNotices.js, 3.24.0) so they show on every page; here the
  // list only highlights the rows that just finished.
  const handleLiveEvents = useCallback(events => {
    // Briefly highlight rows whose progress bar just turned into a result
    const done = events.filter(ev => ev.kind === 'delivered' || ev.kind === 'failed').map(ev => ev.fax.faxId);
    if (done.length) {
      setJustFinished(prev => new Set([...prev, ...done]));
      setTimeout(() => setJustFinished(prev => {
        const next = new Set(prev); done.forEach(id => next.delete(id)); return next;
      }), 2500);
    }
  }, []);

  const { watching, lastChecked, interval } = useLiveOutgoing({
    enabled: tab === 'outgoing' && !loading && (pages.full || pages.page === 1),   // new faxes join the newest page
    data, setData, appliedFilters, getToken,
    onEvents: handleLiveEvents,
  });

  // ── Incoming faxes arriving right now (Incoming tab) ──────────
  // Incoming rules only — these rows never get outgoing actions.
  const arrivingRef = useRef([]);
  // A fax that was arriving has finished: look for it (and anything else
  // new) right away. The new-fax check adds it without reloading the list.
  const checkNowRef = useRef(null);
  const handleArrivalsFinished = useCallback(() => {
    if (tab === 'incoming') checkNowRef.current?.();
  }, [tab]);

  const arriving = useIncomingArrivals({
    enabled: tab === 'incoming', getToken, onFinished: handleArrivalsFinished,
  });
  arrivingRef.current = arriving;

  // Show arriving faxes only when they'd match the filters in use
  const arrivingRows = tab !== 'incoming' || !appliedFilters || pages.page > 1 ? [] : arriving.filter(a => {
    const f = appliedFilters;
    if (f.incomingStatus === 'failed') return false;
    if (f.startDate && (a.startedAt || '') < f.startDate) return false;
    if (f.endDate && (a.startedAt || '') > f.endDate) return false;
    const digits = v => String(v || '').replace(/\D/g, '');
    const match  = (terms, v) => !terms?.length || terms.some(t => digits(v).includes(digits(t)));
    return match(f.fromNumber, a.fromNumber) && match(f.toNumber, a.toNumber);
  });
  // Faxes in line for a busy number (3.36.5; 3.36.6: not in the list, a
  // "Queued · n" pill beside LIVE opens them in a pop-up). Both update by
  // themselves; a fax that starts leaves them and joins the list.
  const nowIso = new Date().toISOString();
  const inLine = tab !== 'outgoing' ? [] : waiting.filter(w => !(w.notBefore && w.notBefore > nowIso))
    .sort((a, b) => (a.etaMin ?? 0) - (b.etaMin ?? 0) || String(a.queuedAt).localeCompare(String(b.queuedAt)));
  const [showLine, setShowLine] = useState(false);
  useEffect(() => { if (!inLine.length) setShowLine(false); }, [inLine.length]);
  useEffect(() => {
    if (!showLine) return undefined;
    const onKey = e => { if (e.key === 'Escape') setShowLine(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showLine]);
  // 3.57.13 (owner): an upper estimate, "N min or less"
  // 3.60.2: Hard to reach (Problem numbers): spaced out (3.61.0: 30–60 minutes apart)
  const inLineWhen = w => (w.position === 1 && (w.etaMin ?? 0) < 3 ? 'next to go out' : `${Math.max(1, Math.ceil(w.etaMin || 0))} min or less`) + (w.slow ? ` · hard to reach: ${tm.hardSpacing.replace(' to ', '–').replace('minutes', 'min').replace('minute', 'min')} apart` : '');
  // Fax Contacts names when hovering a number (3.25.0): only this page's numbers
  // (the other side only: Outbox's To, Inbox's From; Inbox's To is the client's own line)
  const contactName = useContactNames([...data, ...arrivingRows, ...inLine], [tab === 'outgoing' ? 'toNumber' : 'fromNumber']);
  // 3.64.3 (owner): the name shown small under a number (the team's own name
  // in Contacts, else the phone listing; nothing when there's no name)
  const nameUnder = (accountId, number) => { const n = contactName(accountId, number); return n && n !== 'No name' ? <div style={S.nameNote} title={n}>{n}</div> : null; };

  // ── New faxes (both tabs) ─────────────────────────────────────
  // The user counts as busy — so new faxes wait behind a "Show" bar
  // instead of shifting the list — when they have faxes selected, a fax
  // or the Send Fax window open, a confirmation showing, or have scrolled
  // down into the list.
  const tableRef     = useRef(null);
  const selectedRef  = useRef(selected);
  const openIdRef    = useRef(openId);
  const sendFaxRef   = useRef(showSendFax);
  selectedRef.current = selected;
  openIdRef.current   = openId;
  sendFaxRef.current  = showSendFax;
  const isBusy = useCallback(() => (
    prefsRef.current.newFaxes === 'bar'
    || selectedRef.current.size > 0 || !!openIdRef.current || sendFaxRef.current
    || !!document.querySelector('[role=alertdialog]')
    || (tableRef.current ? tableRef.current.getBoundingClientRect().top < -10 : false)
  ), []);

  const [freshIds, setFreshIds] = useState(new Set());
  const markFresh = useCallback(ids => {
    setFreshIds(prev => new Set([...prev, ...ids]));
    setTimeout(() => setFreshIds(prev => {
      const next = new Set(prev); ids.forEach(id => next.delete(id)); return next;
    }), 3000);
  }, []);

  // New incoming faxes just appear in the list; their pop-ups come from the
  // portal-wide watcher (hooks/useFaxNotices.js, 3.24.0)
  const handleFound = useCallback(() => {}, []);

  const { pending, showPending, checkNow } = useNewFaxes({
    tab, enabled: !loading && !!appliedFilters, data, setData, appliedFilters, getToken,
    isBusy, onAdded: markFresh, onFound: handleFound,
  });
  checkNowRef.current = checkNow;

  function showNewFaxes() {
    showPending();
    tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // A pop-up was clicked (3.24.0): open that fax once it's in the list
  useEffect(() => {
    const id = takeOpenFax(tab, data.map(d => d.faxId));
    if (id) setOpenId(id);
  }, [tab, data]);

  // The panel stays open only while its fax is in the current list
  const openIndex = openId ? data.findIndex(d => d.faxId === openId) : -1;

  return (
    <div>
      {/* The tab's heading, as on every tab (3.19.8) */}
      <h2 style={PAGE_TITLE} className="cp-title">{tab === 'outgoing' ? <><Emo e="📤" /> Outbox</> : <><Emo e="📥" /> Inbox</>}</h2>
      {/* Scheduled faxes (3.27.0, Compose → Schedule), with Cancel */}
      {tab === 'outgoing' && waiting.some(w => w.notBefore && w.notBefore > new Date().toISOString()) && (
        <div role="status" style={S.waitingNote}>
          <Emo e="🕒" /> <strong>Scheduled</strong>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {waiting.filter(w => w.notBefore && w.notBefore > new Date().toISOString()).map(w => (
              <li key={w.id}>{formatDate(w.notBefore)}: {w.fileName || 'Fax'} → {w.receiverName ? `${w.receiverName} · ` : ''}{formatFax(w.toNumber)}{w.fromEmail ? ` · ${w.fromName || w.fromEmail}` : ''}
                {w.canCancel && <button onClick={() => cancelWaiting(w)} style={{ marginLeft: 8, background: 'none', border: 'none', color: 'var(--t-c62828)', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit', fontSize: 12 }}>Cancel</button>}</li>
            ))}
          </ul>
        </div>
      )}
      {/* 3.51.0: phones: Filters (opens the search boxes), Show unread, Mark all as seen */}
      {phone && (
        <div style={S.phoneTools}>
          <button onClick={() => setFiltersOpen(o => !o)} aria-expanded={filtersOpen} style={S.filtersBtn}>
            <Emo e="🔍" /> Filters{filtersDiffer(filtersFor(tab, filters), filtersFor(tab, defaultFilters)) && <span style={S.filtersDot} aria-label="some filters are set" />}
            <span style={{ opacity: 0.6, fontSize: 11 }}>{filtersOpen ? '▲' : '▼'}</span>
          </button>
          {tab === 'incoming' && (
            <label style={S.unreadTick}>
              <input type="checkbox" checked={filters.incomingStatus === 'new'}
                onChange={e => { const next = { ...filters, incomingStatus: e.target.checked ? 'new' : 'received' }; setFilters(next); loadData(next); }} /> Show unread
            </label>
          )}
          {tab === 'incoming' && can('inbox.markNew') && !viewAs && data.some(r => r.seen === false) && (
            <button onClick={markAllSeen} disabled={markingAll} style={{ ...S.markAllBtn, marginLeft: 'auto' }}>
              {markingAll ? 'Marking…' : '✓ All seen'}
            </button>
          )}
        </div>
      )}
      {/* Filters */}
      {(!phone || filtersOpen) && <div style={phone ? { ...S.filterBar, ...S.filterBarPhone } : S.filterBar}>
        <div style={phone ? { ...S.filterRow, ...S.filterRowPhone } : S.filterRow}>
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>From Date</label>
            <input type="date" value={filters.startDate}
              onChange={e => handleFilterChange('startDate', e.target.value)} style={S.filterInput} />
          </div>
          <div style={S.filterGroup}>
            <label style={S.filterLabel}>To Date</label>
            <input type="date" value={filters.endDate}
              onChange={e => handleFilterChange('endDate', e.target.value)} style={S.filterInput} />
          </div>

          {tab === 'outgoing' && (
            <>
              <div style={S.filterGroup}>
                <label style={S.filterLabel}>Status</label>
                <select value={filters.status}
                  onChange={e => handleFilterChange('status', e.target.value)} style={S.filterInput}>
                  <option value="">All</option>
                  <option value="queued">Queued</option>
                  <option value="delivered">Delivered</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              {seesOthers && (
                <div style={S.filterGroup}>
                  <label style={S.filterLabel}>Sent By (Email)</label>
                  <MultiSearchSelect suggestType="outgoing" field="fromEmail"
                    values={filters.fromEmail} onChange={v => handleFilterChange('fromEmail', v)}
                    placeholder="name@domain.com" />
                </div>
              )}
            </>
          )}

          {tab === 'incoming' && (
            <>
            <div style={S.filterGroup}>
              <label style={S.filterLabel}>Status</label>
              <select value={filters.incomingStatus}
                onChange={e => handleFilterChange('incomingStatus', e.target.value)} style={S.filterInput}>
                <option value="">All</option>
                <option value="received">Received</option>
                <option value="new">New (not opened yet)</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div style={S.filterGroup}>
              <label style={S.filterLabel}>From Number</label>
              <MultiSearchSelect suggestType="incoming" field="fromNumber"
                values={filters.fromNumber} onChange={v => handleFilterChange('fromNumber', v)}
                placeholder="e.g. 18005551234" />
            </div>
            </>
          )}

          <div style={S.filterGroup}>
            <label style={S.filterLabel}>{tab === 'outgoing' ? 'Sent To (Fax #)' : 'To Number'}</label>
            <MultiSearchSelect key={`${tab}-toNumber`} suggestType={tab} field="toNumber"
              values={filters.toNumber} onChange={v => handleFilterChange('toNumber', v)}
              placeholder="e.g. 18005551234" />
          </div>

          <TeamFilter teams={clientTeams} value={filters.team} onChange={v => handleFilterChange('team', v)}
            groupStyle={S.filterGroup} labelStyle={S.filterLabel} style={S.filterInput} />

          <div style={S.filterGroup}>
              <label style={S.filterLabel}>Show</label>
              <select value={filters.limit}
                onChange={e => handleFilterChange('limit', e.target.value)} style={S.filterInput}>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
                <option value="all">All</option>
              </select>
            </div>

            <div style={{ ...S.filterGroup, flexDirection: 'row', gap: 8, alignSelf: 'flex-end', ...(phone ? { gridColumn: '1 / -1', alignSelf: 'stretch' } : {}) }}>
              <button onClick={() => { loadData(); if (phone) setFiltersOpen(false); }} style={S.searchBtn}><Emo e="🔍" /> Search</button>
              <ResetFiltersButton
                dirty={filtersDiffer(filtersFor(tab, filters), filtersFor(tab, defaultFilters))}
                onReset={resetFilters} />
              {/* 3.49.18: Show unread = the Status filter on New (not opened yet) */}
              {tab === 'incoming' && !phone && (
                <label style={S.unreadTick} title={isAdmin ? `Only received faxes nobody at ${atCo} has opened yet` : 'Only received faxes you haven’t opened yet'}>
                  <input type="checkbox" checked={filters.incomingStatus === 'new'}
                    onChange={e => { const next = { ...filters, incomingStatus: e.target.checked ? 'new' : 'received' }; setFilters(next); loadData(next); }} /> Show unread
                </label>
              )}
              {/* 3.34.0: every new fax you can see becomes seen */}
              {tab === 'incoming' && !phone && can('inbox.markNew') && !viewAs && data.some(r => r.seen === false) && (
                <button onClick={markAllSeen} disabled={markingAll} style={S.markAllBtn} title={isAdmin ? `Mark every new fax as seen for everyone at ${atCo}` : 'Mark every new fax as seen for you'}>
                  {markingAll ? 'Marking…' : '✓ Mark all as seen'}
                </button>
              )}
            </div>
        </div>
      </div>}

      {/* Action bar */}
      <div style={S.actionBar}>
        <div style={S.actionLeft}>
          <span style={S.resultCount}>
            {loading ? 'Loading...' : `${data.length} fax${data.length !== 1 ? 'es' : ''} found`}
          </span>
          {selected.size > 0 && (
            <span style={S.selectedCount}>{selected.size} selected</span>
          )}
          {tab === 'incoming' && !loading && arrivingRows.length > 0 && (
            <span style={S.liveBadge} title={`Checking every ${ARRIVALS_POLL_MS / 1000} seconds`}>
              <span style={S.liveDot} aria-hidden="true" />
              <span style={S.liveLabel}>Live</span>
              <span>{arrivingRows.length} fax{arrivingRows.length !== 1 ? 'es' : ''} arriving now</span>
            </span>
          )}
          {tab === 'outgoing' && !loading && watching > 0 && (
            <span style={S.liveBadge}
              title={`Checking every ${interval / 1000} seconds${lastChecked ? ` · last checked ${`${lastChecked.toLocaleTimeString('en-US', { timeZone: timeZone() })} ${tzLabel(lastChecked)}`}` : ''}`}>
              <span style={S.liveDot} aria-hidden="true" />
              <span style={S.liveLabel}>Live</span>
              <span>Updating {watching} fax{watching !== 1 ? 'es' : ''}</span>
            </span>
          )}
          {tab === 'outgoing' && inLine.length > 0 && (
            <button onClick={() => setShowLine(true)} style={S.queuedBadge} aria-haspopup="dialog"
              title="Faxes waiting for a busy number. They go out one at a time, by themselves. Click to see them.">
              <span style={S.queuedDot} aria-hidden="true" />
              <span style={S.queuedLabel}>Queued</span>
              <span>{inLine.length} fax{inLine.length !== 1 ? 'es' : ''} in line</span>
            </button>
          )}
        </div>
        <div style={S.actionRight}>
          {selected.size > 0 && can('fax.files') && (
            <button onClick={handleBulkDownload} style={S.actionBtn}>
              <Emo e="⬇" /> Download Selected ({selected.size})
            </button>
          )}
          {selected.size > 0 && can('fax.delete') && (
            <button onClick={handleBulkDelete} style={{ ...S.actionBtn, ...S.deleteBtn }}>
              <Emo e="🗑" /> Delete Selected ({selected.size})
            </button>
          )}
          {can('fax.reports') && (
            <button onClick={handleReportDownload} style={{ ...S.actionBtn, ...S.reportBtn }}>
              <Emo e="⬇" /> Download Report (.xlsx)
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && <div style={S.errorBanner}>{error}</div>}

      {/* New faxes waiting while the user is busy */}
      {pending.length > 0 && (
        <button onClick={showNewFaxes} style={S.newFaxesBar} aria-live="polite">
          <Emo e="↑" /> {pending.length} new fax{pending.length !== 1 ? 'es' : ''} · Show
        </button>
      )}

      {/* 3.51.0: phones: one card per fax (tap to open), newest first */}
      {phone && (
        <div style={S.cards} ref={tableRef}>
          {loading ? <LoadingSpinner />
            : data.length === 0 && arrivingRows.length === 0 ? (
              <EmptyState icon={tab === 'outgoing' ? '📤' : '📥'} filtered={resultFiltered} onClear={resetFilters}
                filteredText="No faxes match these filters." emptyText={tab === 'outgoing' ? 'Your Outbox is empty.' : 'Your Inbox is empty.'}
                hint={tab === 'outgoing' && !seesOthers ? 'Your Outbox shows the faxes you sent yourself.' : null} />
            ) : [...arrivingRows.map(a => (
              <div key={`arriving-${a.faxId}`} style={{ ...S.card, background: 'var(--c-f3f9ff)', cursor: 'default' }}>
                <div style={S.cardTop}>
                  <span style={S.cardWho}>{contactName(a.accountId, a.fromNumber) || formatFax(a.fromNumber)}</span>
                  <span style={S.cardWhen}>{formatCardDate(a.startedAt)}</span>
                </div>
                <div style={S.cardMeta}>{showAccount && a.accountName ? `${a.accountName} · ` : ''}<span style={S.arrivingText}>Arriving now…</span></div>
                <FaxProgress fax={{ stage: 'receiving', stageAt: a.startedAt }} compact />
              </div>
            )), ...pages.pageRows.map(fax => {
              const number = tab === 'outgoing' ? fax.toNumber : fax.fromNumber;
              const name = contactName(fax.accountId, number);
              const isNew = tab === 'incoming' && fax.seen === false;
              const meta = [name && formatFax(number), fax.pages ? `${fax.pages} page${fax.pages === 1 ? '' : 's'}` : null,
                tab === 'outgoing' && seesOthers ? (fax.fromName || fax.fromEmail) : null,
                showAccount ? fax.accountName : null, showTeam ? (fax.teamNames || []).join(', ') : null].filter(Boolean);
              return (
                <div key={fax.faxId} role="button" tabIndex={0} onClick={() => setOpenId(fax.faxId)}
                  onKeyDown={e => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); setOpenId(fax.faxId); } }}
                  aria-label={`Open fax details, ${tab === 'outgoing' ? 'to' : 'from'} ${name || formatFax(number)}`}
                  style={{ ...S.card, ...(openId === fax.faxId ? S.cardOpen : {}), ...(freshIds.has(fax.faxId) ? S.freshRow : {}) }}>
                  <div style={S.cardTop}>
                    {isNew && <span style={S.newDot} aria-label="New" />}
                    <span style={{ ...S.cardWho, ...(isNew ? { fontWeight: 800 } : {}) }}>{tab === 'outgoing' ? 'To ' : ''}{name || formatFax(number)}</span>
                    {tab === 'outgoing' && <span onClick={e => e.stopPropagation()}><PriorityStar fax={fax} map={priority} onChanged={reloadPriority} /></span>}
                    <span style={S.cardWhen} title={formatDate(tab === 'outgoing' ? fax.sentAt : fax.receivedAt)}>{formatCardDate(tab === 'outgoing' ? fax.sentAt : fax.receivedAt)}</span>
                  </div>
                  {meta.length > 0 && <div style={S.cardMeta}>{meta.join(' · ')}</div>}
                  {fax.fileName && <div style={S.cardFile}>{fax.fileName}{fax.healthCheck && <span style={S.hcTag}><Emo e="🩺" /> Health check</span>}</div>}
                  <div style={S.cardBottom}>
                    <div style={{ minWidth: 0 }}>
                      {tab === 'outgoing' && fax.status === 'queued' && !fax.requeued ? <FaxProgress fax={fax} compact />
                        : <StatusBadge status={tab === 'outgoing' ? outStatus(fax) : (fax.status || 'received')} failureReason={fax.failureReason} incoming={tab !== 'outgoing'} />}
                      {isNew && <span style={S.newPill}>New</span>}
                      {tab === 'outgoing' && fax.status === 'failed' && !fax.requeued && fax.retryAt && <div style={S.retryNote}>Retrying around {formatRetryWindowDay(fax.retryAt)}</div>}
                      {tab === 'outgoing' && fax.autoRetry && fax.attempt > 1 && <div style={S.retryNote}>Try {fax.attempt}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                      {tab === 'outgoing' && shows('fax.resend') && fax.status === 'failed' && !fax.requeued && !fax.retryAt && (
                        <button onClick={() => handleResend(fax.faxId)} disabled={resending === fax.faxId} style={S.cardBtn} aria-label="Resend this fax">
                          <Emo e={resending === fax.faxId ? '⏳' : '🔄'} />
                        </button>
                      )}
                      {can('fax.files') && !isFailedIncoming(fax) && (
                        <button onClick={() => handleSingleDownload(fax)} disabled={downloading.has(fax.faxId)} style={S.cardBtn} aria-label="Download fax file">
                          <Emo e={downloading.has(fax.faxId) ? '⏳' : '⬇'} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })]}
        </div>
      )}

      {/* Table */}
      {!phone && <div style={S.tableWrap} ref={tableRef}>
        <table style={S.table}>
          <thead>
            <tr style={S.thead}>
              <th style={{ ...S.th, width: 40 }}>
                <input type="checkbox" checked={selected.size === selectable.length && selectable.length > 0}
                  onChange={toggleAll} style={{ cursor: 'pointer' }} />
              </th>
              {showAccount && <SortTh k="account" label="Client" sort={sort} onSort={sortAll} style={S.th} />}
              {showTeam && <SortTh k="team" label="Team" sort={sort} onSort={sortAll} style={S.th} />}
              {tab === 'outgoing' ? (
                <>
                  <SortTh k="date" label="Date & Time" sort={sort} onSort={sortAll} style={S.th} />
                  <SortTh k="from" label="Sent By" sort={sort} onSort={sortAll} style={S.th} />
                  <SortTh k="to" label="Sent To (Fax #)" sort={sort} onSort={sortAll} style={S.th} />
                  <SortTh k="file" label="File Name" sort={sort} onSort={sortAll} style={S.th} />
                  <SortTh k="pages" label="Pages" sort={sort} onSort={sortAll} style={S.th} />
                  <SortTh k="delivered" label="Delivered At" sort={sort} onSort={sortAll} style={S.th} />
                  <SortTh k="status" label="Status" sort={sort} onSort={sortAll} style={S.th} />
                  <th style={S.th}>Download</th>
                </>
              ) : (
                <>
                  <SortTh k="date" label="Received At" sort={sort} onSort={sortAll} style={S.th} />
                  <SortTh k="from" label="From Number" sort={sort} onSort={sortAll} style={S.th} />
                  <SortTh k="to" label="To Number" sort={sort} onSort={sortAll} style={S.th} />
                  <SortTh k="file" label="File Name" sort={sort} onSort={sortAll} style={S.th} />
                  <SortTh k="pages" label="Pages" sort={sort} onSort={sortAll} style={S.th} />
                  <SortTh k="status" label="Status" sort={sort} onSort={sortAll} style={S.th} />
                  <th style={S.th}>Download</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} style={S.emptyCell}><LoadingSpinner /></td></tr>
            ) : data.length === 0 && arrivingRows.length === 0 ? (
              <tr><td colSpan={12} style={S.emptyCell}>
                <EmptyState icon={tab === 'outgoing' ? '📤' : '📥'}
                  filtered={resultFiltered} onClear={resetFilters}
                  filteredText="No faxes match these filters."
                  emptyText={tab === 'outgoing' ? 'Your Outbox is empty.' : 'Your Inbox is empty.'}
                  hint={tab === 'outgoing' && !seesOthers ? 'Your Outbox shows the faxes you sent yourself.' : null} />
              </td></tr>
            ) : [...arrivingRows.map(a => (
              <tr key={`arriving-${a.faxId}`} style={{ ...S.tr, background: 'var(--c-f3f9ff)' }}
                title="This fax is arriving now. It can be opened once it has been received.">
                <td style={S.td}><input type="checkbox" disabled aria-label="Not available while the fax is arriving" /></td>
                {showAccount && <td style={S.td}>{a.accountName || '—'}</td>}
                {showTeam && <td style={S.td}>{a.teamName || ''}</td>}   {/* 3.59.3: the team whose number it is */}
                <td style={S.td}><span style={S.dateText} title={formatDate(a.startedAt)}>{formatShortDate(a.startedAt)}</span></td>
                <td style={S.td}><span style={S.faxNum} title={contactName(a.accountId, a.fromNumber) || undefined}>{formatFax(a.fromNumber)}</span></td>
                <td style={S.td}><span style={S.faxNum} title="Your fax line">{formatFax(a.toNumber)}</span></td>
                <td style={S.td}><span style={S.arrivingText}>Arriving now…</span></td>
                <td style={{ ...S.td, textAlign: 'center' }}>—</td>
                <td style={S.td}><FaxProgress fax={{ stage: 'receiving', stageAt: a.startedAt }} compact /></td>
                <td style={S.td} />
              </tr>
            )), ...pages.pageRows.map((fax, i) => (
              <tr key={fax.faxId}
                onClick={() => setOpenId(fax.faxId)}
                onKeyDown={e => {
                  // preventDefault stops the same key press from activating
                  // the panel's Close button once focus moves there
                  if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                    e.preventDefault();
                    setOpenId(fax.faxId);
                  }
                }}
                tabIndex={0}
                aria-label={`Open fax details, ${tab === 'outgoing' ? 'to ' + formatFax(fax.toNumber) : 'from ' + formatFax(fax.fromNumber)}`}
                style={{
                  ...S.tr, cursor: 'pointer',
                  background: openId === fax.faxId ? 'var(--c-e0f2f1)' : (i % 2 === 0 ? 'var(--c-fafafa)' : 'var(--c-ffffff)'),
                  boxShadow: openId === fax.faxId ? 'inset 3px 0 0 var(--c-00897b)' : 'none',
                  ...(freshIds.has(fax.faxId) ? S.freshRow : {})
                }}>
                <td style={S.td} onClick={e => e.stopPropagation()}>
                  <input type="checkbox" checked={selected.has(fax.faxId)}
                    disabled={isFailedIncoming(fax)}
                    onChange={() => toggleSelect(fax.faxId)}
                    style={{ cursor: isFailedIncoming(fax) ? 'not-allowed' : 'pointer' }} />
                </td>
                {showAccount && <td style={S.td}><span style={S.accountText}>{fax.accountName || '—'}</span></td>}
                {showTeam && <td style={S.td}><span style={S.accountText}>{(fax.teamNames || []).join(', ') || '—'}</span></td>}
                {tab === 'outgoing' ? (
                  <>
                    <td style={S.td}><span style={S.dateText} title={formatDate(fax.sentAt)}>{formatShortDate(fax.sentAt)}</span></td>
                    <td style={S.td}><span style={S.emailText} title={fax.fromEmail}>{fax.fromName || fax.fromEmail}</span></td>
                    <td style={{ ...S.td, whiteSpace: 'nowrap' }}><span style={S.faxNum} title={contactName(fax.accountId, fax.toNumber) || undefined}>{formatFax(fax.toNumber)}</span><PriorityStar fax={fax} map={priority} onChanged={reloadPriority} />{nameUnder(fax.accountId, fax.toNumber)}</td>
                    <td style={S.td}><span style={S.fileName} title={fax.fileName}>{fax.fileName}</span>{fax.healthCheck && <span style={S.hcTag} title="The automatic test fax between our test lines (the Health page in the Super Admin menu). No emails, not billed."><Emo e="🩺" /> Health check</span>}</td>
                    <td style={{ ...S.td, textAlign: 'center' }}>{fax.pages || '—'}</td>
                    <td style={S.td}><span style={S.dateText} title={fax.deliveredAt ? formatDate(fax.deliveredAt) : undefined}>{fax.deliveredAt ? formatShortDate(fax.deliveredAt) : '—'}</span></td>
                    <td style={{ ...S.td, ...(justFinished.has(fax.faxId) ? S.justFinished : {}) }}>
                      {fax.status === 'queued' && !fax.requeued ? (
                        <FaxProgress fax={fax} compact />
                      ) : (
                        <span style={justFinished.has(fax.faxId) ? S.badgePop : undefined}>
                          <StatusBadge status={outStatus(fax)} failureReason={fax.failureReason} />
                        </span>
                      )}
                      {fax.status === 'failed' && !fax.requeued && fax.retryAt && (
                        <div style={S.retryNote}>Retrying around {formatRetryWindowDay(fax.retryAt)}</div>
                      )}
                      {/* 3.78.0 (owner): one row per document; the earlier tries are in the fax's panel */}
                      {fax.autoRetry && fax.attempt > 1 && <div style={S.retryNote} title="Earlier tries are in the fax's details">Try {fax.attempt}</div>}
                    </td>
                  </>
                ) : (
                  <>
                    <td style={S.td}><span style={S.dateText} title={formatDate(fax.receivedAt)}>{formatShortDate(fax.receivedAt)}</span></td>
                    <td style={S.td}>
                      {fax.seen === false && <span style={S.newDot} aria-label="New: not opened yet" title={isAdmin ? `New: nobody at ${atCo} has opened it yet` : 'New: you haven’t opened it yet'} />}
                      <span style={{ ...S.faxNum, ...(fax.seen === false ? { fontWeight: 800 } : {}) }} title={contactName(fax.accountId, fax.fromNumber) || undefined}>{formatFax(fax.fromNumber)}</span>
                      {nameUnder(fax.accountId, fax.fromNumber)}
                    </td>
                    {/* 3.64.3 (owner): under your line, its name (Settings → Fax Lines) or your company's name */}
                    <td style={S.td}><span style={S.faxNum} title="Your fax line">{formatFax(fax.toNumber)}</span>{fax.lineName && <div style={S.nameNote} title={fax.lineName}>{fax.lineName}</div>}</td>
                    <td style={S.td}><span style={S.fileName} title={fax.fileName}>{fax.fileName}</span>{fax.healthCheck && <span style={S.hcTag} title="The automatic test fax between our test lines (the Health page in the Super Admin menu). No emails, not billed."><Emo e="🩺" /> Health check</span>}</td>
                    <td style={{ ...S.td, textAlign: 'center' }}>{fax.pages || '—'}</td>
                    <td style={S.td}><StatusBadge status={fax.status || 'received'} failureReason={fax.failureReason} incoming />{fax.seen === false && <span style={S.newPill}>New</span>}</td>
                  </>
                )}
                <td style={S.td} onClick={e => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {can('fax.files') && <button
                      onClick={() => handleSingleDownload(fax)}
                      disabled={downloading.has(fax.faxId) || isFailedIncoming(fax)}
                      style={isFailedIncoming(fax) ? { ...S.dlBtn, opacity: 0.35, cursor: 'not-allowed' } : S.dlBtn}
                      title={isFailedIncoming(fax) ? 'No document — this fax failed before it was received' : 'Download fax file'}
                      aria-label="Download fax file"
                    >
                      <Emo e={downloading.has(fax.faxId) ? '⏳' : '⬇'} />
                    </button>}
                    {/* No manual resend while an automatic retry is scheduled */}
                    {tab === 'outgoing' && shows('fax.resend') && fax.status === 'failed' && !fax.requeued && !fax.retryAt && (
                      <button
                        onClick={() => handleResend(fax.faxId)}
                        disabled={resending === fax.faxId}
                        style={S.resendBtn}
                        title="Resend this fax"
                        aria-label="Resend this fax"
                      >
                        <Emo e={resending === fax.faxId ? '⏳' : '🔄'} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))]}
          </tbody>
        </table>
      </div>}
      {!loading && <Pager {...pages.pager} label="faxes" />}

      {data.length > 0 && (
        <div style={S.refNote}>
          {phone ? 'Tap a fax to preview it and see its details and history.' : 'Click any fax to preview it and see its details and history.'}
        </div>
      )}

      {/* Faxes in line (3.36.6): the pop-up behind the Queued pill */}
      {showLine && inLine.length > 0 && (
        <div style={S.overlay} onMouseDown={e => { if (e.target === e.currentTarget) setShowLine(false); }}>
          <div style={S.modal} role="dialog" aria-modal="true" aria-label="Faxes in line">
            <div style={S.modalHead} className="dlg-head">
              <strong style={{ color: 'var(--t-e65100)', fontSize: 16, flex: 1 }}><Emo e="⏳" /> Faxes in line · {inLine.length}</strong>
              <button onClick={() => setShowLine(false)} style={S.closeBtn} aria-label="Close">✕</button>
            </div>
            <div style={{ padding: '6px 18px 4px', fontSize: 13, color: 'var(--t-5d4037)' }}>
              A fax machine takes one call at a time, so faxes to the same number go out one after another. These go out by themselves in their turn, and this list updates by itself.{inLine.some(w => w.resendOf) ? ' Click a resend to see its fax and history.' : ''}
            </div>
            <div style={{ padding: '6px 18px 16px', overflow: 'auto' }}>
              <table style={{ ...S.table, minWidth: 760 }}>
                <thead>
                  <tr style={S.thead}>
                    {showAccount && <th style={S.th}>Client</th>}
                    <th style={S.th}>In line since</th>
                    <th style={S.th}>Sent By</th>
                    <th style={S.th}>Sent To (Fax #)</th>
                    <th style={S.th}>File Name</th>
                    <th style={S.th}>Pages</th>
                    <th style={S.th}>Status</th>
                    {inLine.some(w => w.canSendNow) && <th style={S.th}><span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Actions</span></th>}
                  </tr>
                </thead>
                <tbody>
                  {inLine.map((w, i) => (
                    <tr key={w.id} onClick={w.resendOf ? () => { setShowLine(false); setLineFax(w); } : undefined} title={w.resendOf ? 'Open this fax' : undefined}
                      style={{ ...S.tr, background: i % 2 === 0 ? 'var(--c-fffaf0)' : 'var(--c-ffffff)', ...(w.resendOf ? { cursor: 'pointer' } : {}) }}>
                      {showAccount && <td style={S.td}><span style={S.accountText}>{(effectiveInfo?.accounts || []).find(x => x.id === w.accountId)?.name || w.accountId || '—'}</span></td>}
                      <td style={S.td}><span style={S.dateText} title={formatDate(w.queuedAt)}>{formatShortDate(w.queuedAt)}</span></td>
                      <td style={S.td}><span style={S.emailText} title={w.fromEmail}>{w.fromName || w.fromEmail}</span></td>
                      <td style={{ ...S.td, whiteSpace: 'nowrap' }}><span style={S.faxNum} title={contactName(w.accountId, w.toNumber) || undefined}>{formatFax(w.toNumber)}</span>{nameUnder(w.accountId, w.toNumber)}</td>
                      <td style={S.td}><span style={S.fileName} title={w.fileName}>{w.fileName || 'Fax'}</span></td>
                      <td style={{ ...S.td, textAlign: 'center' }}>{w.pages || '—'}</td>
                      <td style={S.td}>
                        <span style={S.inLinePill}>Queued{w.position ? ` · ${w.position} of ${w.of}` : ''}</span>
                        {w.position && <div style={S.retryNote}>{inLineWhen(w)}</div>}
                        {w.resendOf && <div style={S.retryNote}>Resend</div>}
                      </td>
                      {inLine.some(x => x.canSendNow) && (
                        <td style={{ ...S.td, whiteSpace: 'nowrap', textAlign: 'right' }}>
                          {w.canSendNow && <button onClick={e => { e.stopPropagation(); sendWaitingNow(w); }} style={{ ...S.resendBtn, fontSize: 13, fontWeight: 700, fontFamily: 'inherit', color: 'var(--t-e65100)' }} title="Send it now instead of waiting in line"><Emo e="🔄" /> Send now</button>}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Floating Send Fax Button */}
      {/* The floating Send Fax button became the Compose Fax tab (3.20.0) */}

      {/* 3.69.4 (owner): a Resend in line opens its failed fax and history */}
      {lineFax && openIndex < 0 && (
        <FaxDetailPanel type="outgoing"
          row={{ faxId: lineFax.resendOf, toNumber: lineFax.toNumber, fileName: lineFax.fileName, pages: lineFax.pages, fromEmail: lineFax.fromEmail, accountId: lineFax.accountId, status: 'failed', requeued: true }}
          onClose={() => setLineFax(null)} hasPrev={false} hasNext={false} onPrev={() => {}} onNext={() => {}}
          onResend={handleResend} onRetryCancelled={() => loadRef.current?.()} />
      )}

      {openIndex >= 0 && (
        <FaxDetailPanel
          type={tab}
          row={data[openIndex]}
          onClose={() => setOpenId(null)}
          hasPrev={openIndex > 0}
          hasNext={openIndex < data.length - 1}
          onPrev={() => setOpenId(data[openIndex - 1].faxId)}
          onNext={() => setOpenId(data[openIndex + 1].faxId)}
          onResend={handleResend}
          onDeleted={ids => {
            setOpenId(null);
            setData(prev => prev.filter(r => !ids.includes(r.faxId)));
            setSelected(prev => new Set([...prev].filter(id => !ids.includes(id))));
          }}
          onUpdate={(id, patch) => setData(prev => prev.map(r => (r.faxId === id ? { ...r, ...patch } : r)))}
          onRetryCancelled={id => setData(prev => prev.map(r => (
            r.faxId === id ? { ...r, retryAt: null, retryCancelled: isAdmin ? 'admin' : 'user' } : r
          )))}
        />
      )}

      {showSendFax && (
        <SendFaxModal
          onClose={() => setShowSendFax(false)}
          onSuccess={() => { if (tab === 'outgoing') loadData(); }}
        />
      )}
    </div>
  );
}

function StatusBadge({ status, failureReason, incoming = false }) {
  const s        = STATUS_COLORS[status] || { bg: 'var(--c-f5f5f5)', color: 'var(--t-666666)' };
  const isFailed = status === 'failed' && failureReason;
  const [show, setShow] = useState(false);

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{
        background: s.bg, color: s.color, padding: '3px 10px', borderRadius: 20,
        fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px'
      }}>
        {status}
      </span>

      {isFailed && (
        <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          <span
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
            onClick={() => setShow(v => !v)}
            style={{
              width: 18, height: 18, borderRadius: '50%',
              background: 'var(--c-b71c1c)', color: 'var(--t-ffffff)',
              fontSize: 11, fontWeight: 700, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              userSelect: 'none', flexShrink: 0
            }}>
            i
          </span>
          {show && (
            <div style={{
              position    : 'absolute', bottom: '130%', left: '50%',
              transform   : 'translateX(-50%)',
              background  : 'var(--c-2d2d2d)', color: 'var(--t-ffffff)', padding: '10px 14px',
              borderRadius: 8, fontSize: 12, zIndex: 999,
              boxShadow   : '0 4px 16px rgb(var(--c-000000-rgb) / 0.25)',
              minWidth    : 200, maxWidth: 300, lineHeight: 1.6,
              pointerEvents: 'none'
            }}>
              <strong style={{ display: 'block', marginBottom: 4, color: 'var(--t-ffcdd2)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Failure Reason</strong>
              {/* 3.57.7: plain words (the same text as the emails) */}
              {incoming ? incomingReasonText(failureReason) : reasonText(failureReason)}   {/* 3.62.23: received faxes from our side */}
              <div style={{
                position: 'absolute', top: '100%', left: '50%',
                transform: 'translateX(-50%)',
                borderWidth: 6, borderStyle: 'solid',
                borderColor: 'var(--c-2d2d2d) transparent transparent transparent'
              }} />
            </div>
          )}
        </span>
      )}
    </span>
  );
}

// 3.40.1: the portal's shared loading look (components/Loader)
function LoadingSpinner() {
  return <Loader label="Loading faxes…" />;
}

const S = {
  // 3.51.0: phones
  phoneTools: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 12 },
  filtersBtn: { position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 40, padding: '8px 14px', borderRadius: 10, border: '1.5px solid var(--c-b2dfdb)', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', fontWeight: 700, fontSize: 14, fontFamily: 'inherit', cursor: 'pointer' },
  filtersDot: { width: 8, height: 8, borderRadius: '50%', background: 'var(--c-ef6c00)' },
  filterBarPhone: { padding: 14 },
  filterRowPhone: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 },
  cards: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: { display: 'flex', flexDirection: 'column', gap: 4, padding: '12px 14px', borderRadius: 14, background: 'var(--c-ffffff)', border: '1px solid var(--c-e0e0e0)', boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.05)', cursor: 'pointer', WebkitTapHighlightColor: 'transparent' },
  cardOpen: { border: '1px solid var(--c-00897b)', boxShadow: 'inset 3px 0 0 var(--c-00897b)' },
  cardTop: { display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 },
  cardWho: { flex: 1, minWidth: 0, fontSize: 15, fontWeight: 700, color: 'var(--t-263238)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardWhen: { flexShrink: 0, fontSize: 12, color: 'var(--t-78909c)', whiteSpace: 'nowrap' },
  cardMeta: { fontSize: 12.5, color: 'var(--t-607d8b)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardFile: { fontSize: 12.5, color: 'var(--t-546e7a)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardBottom: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 4 },
  cardBtn: { width: 40, height: 40, borderRadius: 10, border: '1px solid var(--c-b2dfdb)', background: 'var(--c-f0faf8)', fontSize: 17, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  newDot: { display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--c-00897b)', marginRight: 6, verticalAlign: 'middle' },
  newPill: { marginLeft: 6, fontSize: 10, fontWeight: 800, color: 'var(--t-00695c)', background: 'var(--c-e0f2f1)', borderRadius: 10, padding: '1px 7px', verticalAlign: 'middle' },
  unreadTick: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--t-37474f)', cursor: 'pointer', whiteSpace: 'nowrap', padding: '0 4px' },
  markAllBtn: { padding: '8px 14px', background: 'var(--c-ffffff)', color: 'var(--t-00695c)', border: '1.5px solid var(--c-b2dfdb)', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', whiteSpace: 'nowrap' },
  waitingNote: { background: 'var(--c-fff8e1)', border: '1.5px solid var(--c-ffe082)', borderRadius: 10, padding: '10px 14px', margin: '0 0 14px', fontSize: 13, color: 'var(--t-5d4037)' },
  filterBar: {
    background: 'var(--c-ffffff)', borderRadius: 12, padding: '20px 24px', marginBottom: 20,
    boxShadow: '0 1px 4px rgb(var(--c-000000-rgb) / 0.06)', border: '1px solid var(--b-e8f5e9)'
  },
  filterRow: { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 160 },
  filterLabel: { fontSize: 12, fontWeight: 600, color: 'var(--t-546e7a)', textTransform: 'uppercase', letterSpacing: '0.5px' },
  filterInput: {
    padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)',
    fontSize: 13, color: 'var(--t-333333)', fontFamily: 'inherit', background: 'var(--c-ffffff)'
  },
  searchBtn: {
    padding: '9px 20px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none',
    borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600,
    fontFamily: 'inherit', whiteSpace: 'nowrap'
  },
  actionBar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 12, flexWrap: 'wrap', gap: 10
  },
  actionLeft: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', minWidth: 0 },   // 3.51.0: wraps on phones
  actionRight: { display: 'flex', gap: 10, flexWrap: 'wrap' },
  resultCount: { fontSize: 14, color: 'var(--t-546e7a)', whiteSpace: 'nowrap' },
  // Prominent live indicator: bold, bordered and softly glowing
  liveBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--c-e8f5e9)',
    color: 'var(--t-1b5e20)', padding: '6px 14px', borderRadius: 22, fontSize: 14, fontWeight: 600,
    border: '1.5px solid var(--c-66bb6a)', animation: 'liveGlow 2s ease-in-out infinite'
  },
  liveDot: {
    width: 11, height: 11, borderRadius: '50%', background: 'var(--c-2e7d32)', flexShrink: 0,
    boxShadow: '0 0 0 3px rgb(var(--c-43a047-rgb) / 0.25)', animation: 'livePulse 1.6s ease-in-out infinite'
  },
  liveLabel: {
    fontSize: 12, fontWeight: 800, letterSpacing: '0.8px', textTransform: 'uppercase',
    background: 'var(--c-2e7d32)', color: 'var(--t-ffffff)', padding: '2px 8px', borderRadius: 10
  },
  justFinished: { animation: 'rowFlash 2.5s ease-out' },
  freshRow: { animation: 'rowFlash 3s ease-out' },
  newFaxesBar: {
    display: 'block', width: '100%', marginBottom: 10, padding: '10px 16px',
    background: 'var(--c-1565c0)', color: 'var(--t-ffffff)', border: 'none', borderRadius: 10, cursor: 'pointer',
    fontSize: 14, fontWeight: 700, fontFamily: 'inherit', boxShadow: '0 4px 14px rgb(var(--c-1565c0-rgb) / 0.35)',
    animation: 'badgePop 0.3s ease-out'
  },
  badgePop: { display: 'inline-block', animation: 'badgePop 0.45s ease-out' },
  arrivingText: { color: 'var(--t-1565c0)', fontStyle: 'italic', fontWeight: 500 },
  // Queued pill (3.36.6): beside LIVE, same shape, amber
  queuedBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--c-fff3e0)', color: 'var(--t-bf360c)',
    padding: '6px 14px', borderRadius: 22, fontSize: 14, fontWeight: 600, border: '1.5px solid var(--c-ffb74d)',
    cursor: 'pointer', fontFamily: 'inherit',
  },
  queuedDot: { width: 11, height: 11, borderRadius: '50%', background: 'var(--c-fb8c00)', flexShrink: 0, boxShadow: '0 0 0 3px rgb(var(--c-fb8c00-rgb) / 0.22)', animation: 'livePulse 1.6s ease-in-out infinite' },
  queuedLabel: { fontSize: 12, fontWeight: 800, letterSpacing: '0.8px', textTransform: 'uppercase', background: 'var(--c-bf360c)', color: 'var(--t-ffffff)', padding: '2px 8px', borderRadius: 10 },
  overlay: { position: 'fixed', inset: 0, background: 'rgb(var(--c-0f2823-rgb) / 0.45)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '8vh 16px 16px' },
  modal: { background: 'var(--c-ffffff)', borderRadius: 14, width: 'min(1000px, 100%)', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgb(var(--c-000000-rgb) / 0.25)' },
  modalHead: { display: 'flex', gap: 10, alignItems: 'center', padding: '14px 18px', borderBottom: '2px solid var(--c-ffe0b2)' },
  closeBtn: { width: 34, height: 34, borderRadius: 8, border: '1.5px solid var(--c-e0e0e0)', background: 'var(--c-ffffff)', cursor: 'pointer', fontSize: 15, color: 'var(--t-546e7a)', fontFamily: 'inherit', flexShrink: 0 },
  inLinePill: { background: 'var(--c-fff3e0)', color: 'var(--t-e65100)', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  retryNote: { fontSize: 11, color: 'var(--t-e65100)', fontWeight: 600, marginTop: 4, whiteSpace: 'nowrap' },
  nameNote: { fontSize: 11, color: 'var(--t-00897b)', fontWeight: 700, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 190 },   // 3.64.3
  selectedCount: {
    background: 'var(--c-e0f2f1)', color: 'var(--t-00695c)', padding: '3px 12px',
    borderRadius: 20, fontSize: 13, fontWeight: 600
  },
  actionBtn: {
    padding: '8px 16px', background: 'var(--c-00897b)', color: 'var(--t-ffffff)', border: 'none',
    borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit'
  },
  reportBtn: { background: 'var(--c-37474f)' },
  deleteBtn: { background: 'var(--c-ffffff)', color: 'var(--t-b71c1c)', border: '1.5px solid var(--c-ef9a9a)' },
  errorBanner: {
    background: 'var(--c-fce4ec)', color: 'var(--t-b71c1c)', padding: '12px 18px',
    borderRadius: 8, marginBottom: 16, fontSize: 14
  },
  accountText: { fontSize: 12, fontWeight: 600, color: 'var(--t-546e7a)', whiteSpace: 'nowrap' },
  // Scroll sideways on narrow screens instead of clipping columns
  tableWrap: {
    background: 'var(--c-ffffff)', borderRadius: 12, overflowX: 'auto',
    boxShadow: '0 1px 6px rgb(var(--c-000000-rgb) / 0.07)', border: '1px solid var(--b-e8f5e9)'
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'var(--c-00695c)' },
  th: {
    padding: '13px 14px', textAlign: 'left', color: 'var(--t-ffffff)',
    fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.5px', whiteSpace: 'nowrap'
  },
  tr: { borderBottom: '1px solid var(--c-f0f0f0)' },
  td: { padding: '12px 14px', fontSize: 13, color: 'var(--t-333333)', verticalAlign: 'middle' },
  dateText: { color: 'var(--t-546e7a)', fontSize: 12, whiteSpace: 'nowrap' },
  emailText: { color: 'var(--t-00695c)', fontWeight: 500 },
  faxNum: { fontFamily: 'monospace', fontSize: 13, color: 'var(--t-37474f)' },
  // 3.41.0: the daily test fax between our test lines
  hcTag: { display: 'inline-block', marginLeft: 6, padding: '1px 8px', borderRadius: 10, background: 'var(--c-ede7f6)', color: 'var(--t-4527a0)', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' },
  fileName: {
    display: 'inline-block', maxWidth: 160, overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--t-1565c0)', cursor: 'default'
  },
  dlBtn: {
    background: 'none', border: '1.5px solid var(--c-e0e0e0)', borderRadius: 8,
    padding: '5px 10px', cursor: 'pointer', fontSize: 16
  },
  emptyCell: { textAlign: 'center', padding: '40px 0' },
  refNote    : { fontSize: 12, color: 'var(--t-b0bec5)', marginTop: 10, textAlign: 'right' },
  resendBtn  : {
    background: 'none', border: '1.5px solid var(--c-f9a825)', borderRadius: 8,
    padding: '5px 10px', cursor: 'pointer', fontSize: 16, title: 'Resend fax'
  }
};